import React from 'react';
import { BASE_URL } from '../helper/constants';
import { roomUnreadCount } from '../pages/Project/roomDnd';
import MaterialIcon from './MaterialIcon';
import './CompanySelector.css';

// Badge count from Kjuup+ projects only, so it appears only when the Kjuup+
// section actually has unread rooms (chats + unread reports).
const kjuupPlusUnreadCount = (item, showDailyBriefingCounters) => {
  if (!item.userProjectList || item.userProjectList.length === 0) {
    return 0;
  }
  return item.userProjectList.reduce(
    (total, project) => total + roomUnreadCount(project, showDailyBriefingCounters),
    0
  );
};

// Declared at module scope on purpose. Defining it inside CompanySelector made
// it a new component type on every render, so React unmounted and remounted
// every chip each time — and a remount between mousedown and mouseup destroys
// the node the press started on, which stops the browser firing the click.
const CompanyTab = ({ item, index, onSelect, unseenArticleByCompany, showDailyBriefingCounters }) => {
  const isSelected = item.isSelected;
  const isDisabled = item.isLeave;

  // On the article feed the chip counts unseen articles for that company; on
  // the project section it counts unread Kjuup+ chats and unread reports.
  const badgeCount = unseenArticleByCompany
    ? (unseenArticleByCompany[item.id] || 0)
    : kjuupPlusUnreadCount(item, showDailyBriefingCounters);
  const hasUnread = badgeCount > 0;

  return (
    <div
      id={item.id}
      role="button"
      onPointerDown={(e) => {
        if (isDisabled) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        onSelect?.(index, item.id);
      }}
      onClick={() => !isDisabled && onSelect?.(index, item.id)}
      className={`kj-tab company-selector-tab ${isSelected ? 'is-active' : ''} ${isDisabled ? 'company-selector-tab-disabled' : ''}`}
      title={item.name}
    >
      {/* Company Logo */}
      <div className="company-selector-logo-container">
        {item.logo ? (
          <img
            src={BASE_URL + item.logo}
            alt={item.name}
            className="company-selector-logo"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div
          className={`company-selector-logo-fallback company-selector__logo-fallback${item.logo ? ' company-selector__logo-fallback--hidden' : ''}`}
        >
          <MaterialIcon
            icon="domain"
            size={16}
            color="#737373"
            weight={300}
          />
        </div>
      </div>

      {/* Company Name */}
      <div className="company-selector-name">
        <span className="company-selector-name-text">
          {item.name}
        </span>
      </div>

      {/* Unread Badge - unseen articles on the feed, Kjuup+ chats elsewhere */}
      {hasUnread && (
        <div className="company-selector-badge">
          <span>{badgeCount > 99 ? '99+' : badgeCount}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Enhanced company selector component for Kjuup+ companies
 * Button-based layout that wraps to multiple lines if needed
 *
 * @param {Object} props
 * @param {Array} props.companies - Array of company objects with id, name, logo, isSelected, isLeave, totalUnreadChat
 * @param {Function} props.onSelect - Callback when a company is selected (index, id)
 * @param {Object} props.apiRef - Not used anymore (kept for backward compatibility)
 * @param {boolean} props.showArrows - Not used anymore (kept for backward compatibility)
 * @param {Object} props.unseenArticleByCompany - { [companyId]: count } of unseen
 *        articles. Only set on the article feed; the project section leaves it
 *        empty and keeps showing unread project chats / reports instead.
 * @param {boolean} props.showDailyBriefingCounters - include unread daily
 *        briefings on project-section company chips (same gate as room rows).
 */
const CompanySelector = ({
  companies = [],
  onSelect,
  apiRef,
  showArrows = true,
  unseenArticleByCompany = null,
  showDailyBriefingCounters = false,
}) => {
  if (!companies || companies.length === 0) {
    return null;
  }

  // Don't render if only one company
  if (companies.length === 1) {
    return null;
  }

  return (
    <div className="company-selector-container">
      <div className="company-selector-buttons-wrapper">
        {companies.map((item, index) => (
          <CompanyTab
            key={item.id}
            item={item}
            index={index}
            onSelect={onSelect}
            unseenArticleByCompany={unseenArticleByCompany}
            showDailyBriefingCounters={showDailyBriefingCounters}
          />
        ))}
      </div>
    </div>
  );
};

export default CompanySelector;
